import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Package,
  Truck,
  AlertTriangle,
  Search,
  Plus,
  PlusCircle,
  Trash2,
  Check,
  ArrowRight,
  ArrowLeft,
  FileText,
  DollarSign,
  Tag,
  History,
  Sparkles,
  Settings,
  Loader2,
  Printer,
  Save,
  FolderOpen,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ShelfTalkerModal from "@/components/ShelfTalkerModal";
import GRVHistoryModal from "@/components/GRVHistoryModal";
import AiInvoiceImportModal from "@/components/AiInvoiceImportModal";
import { LocationInput, formatLocation, parseLocation } from "@/components/LocationInput";
import { ProductDetailModal, defaultProduct } from "@/components/products";
import { printStockLabelsForGRV, buildStockLabelsHtmlForGRV } from "@/utils/stockLabelPrint";
import { submitPrintJob } from "@/lib/printJobs";
import { GrvPasteImageButton } from "@/components/grv/GrvPasteImageButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GRV_DRAFT_KEY = "grv_draft_v1";

const ORDER_TYPES = [
  { value: 1, label: "Standard Stock Order", description: "Regular stock items" },
  { value: 2, label: "Direct Stock Order", description: "Stock not normally kept" },
];

export const StockReceiving = () => {
  // Screen state: "header" or "items"
  const [currentScreen, setCurrentScreen] = useState("header");
  
  // Header form state
  const [orderType, setOrderType] = useState(1);
  const [poNumber, setPoNumber] = useState("");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isCod, setIsCod] = useState(false);  // Default to NOT COD as most suppliers are not COD
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split("T")[0]);
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState("");
  const [invoiceAmountType, setInvoiceAmountType] = useState("excl");
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [costPricesIncludeVat, setCostPricesIncludeVat] = useState(false);  // NEW: Track if entered cost prices include VAT
  const [deliveryFee, setDeliveryFee] = useState("");  // Delivery fee to distribute across items
  const [deliveryFeeIncludesVat, setDeliveryFeeIncludesVat] = useState(false);  // Whether delivery fee includes VAT
  const [includeDeliveryFeeInTotals, setIncludeDeliveryFeeInTotals] = useState(false);  // Default OFF per user choice
  const [notes, setNotes] = useState("");
  
  // Validation result from header
  const [headerValidation, setHeaderValidation] = useState(null);
  const [supplierSearchResults, setSupplierSearchResults] = useState([]);
  const [showSupplierSearch, setShowSupplierSearch] = useState(false);
  
  // Items state
  const [items, setItems] = useState([]);
  const [missingImageIds, setMissingImageIds] = useState(new Set());
  const itemIdsKey = [...new Set(items.map((i) => i.product_id).filter((id) => id && !String(id).startsWith("once-off-")))].sort().join(",");
  useEffect(() => {
    if (!itemIdsKey) return setMissingImageIds(new Set());
    axios.post(`${API}/product-images/missing`, itemIdsKey.split(","))
      .then((r) => setMissingImageIds(new Set(r.data.missing)))
      .catch(() => {});
  }, [itemIdsKey]);
  const [aiImportOpen, setAiImportOpen] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [showProductSearch, setShowProductSearch] = useState(false);
  
  // New product modal state - using full ProductDetailModal
  const [showNewProductModal, setShowNewProductModal] = useState(false);
  const [newProductForModal, setNewProductForModal] = useState(null);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Fetch categories and departments for the product form
  const fetchCategoriesDepartments = async () => {
    try {
      const response = await axios.get(`${API}/categories-departments`);
      setCategories(response.data.categories || []);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };
  
  // Refresh categories/departments when opening product modal
  const handleRefreshLists = () => {
    fetchCategoriesDepartments();
  };
  
  // Price increase alerts
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [showPriceAlerts, setShowPriceAlerts] = useState(false);
  
  // Special price alerts (when cost is cheaper than normal)
  const [specialsThreshold, setSpecialsThreshold] = useState(10); // Default 10%
  const [specials, setSpecials] = useState([]);
  const [showSpecialsTab, setShowSpecialsTab] = useState(false);
  
  // Auto-detect specials whenever items change - with debouncing to avoid false alerts during typing
  const prevSpecialsRef = useRef([]);
  const specialsTimeoutRef = useRef(null);

  // Self-heal: whenever the invoice amount type is "excl", the
  // costPricesIncludeVat flag must be false. Any prior state (e.g. left
  // over from the AI-invoice-import path, or after toggling modes) is
  // reset here so the math is always consistent with the mode shown to
  // the user. Item cost values themselves are left untouched — the
  // calculateTotals guard treats them as excl VAT in this mode.
  useEffect(() => {
    if (invoiceAmountType === "excl" && costPricesIncludeVat) {
      setCostPricesIncludeVat(false);
    }
  }, [invoiceAmountType, costPricesIncludeVat]);

  // Auto-include delivery fee in GRV totals as soon as a non-zero fee is
  // typed. Previously this checkbox defaulted to OFF and users would enter
  // a fee, watch the GRV never balance (variance always == fee), and blame
  // the "delivery fee" logic. If they intentionally want it excluded from
  // totals (rare) they can still untick after typing.
  useEffect(() => {
    const fee = parseFloat(deliveryFee) || 0;
    if (fee > 0 && !includeDeliveryFeeInTotals) {
      setIncludeDeliveryFeeInTotals(true);
    }
  }, [deliveryFee, includeDeliveryFeeInTotals]);

  
  useEffect(() => {
    // Clear any pending timeout
    if (specialsTimeoutRef.current) {
      clearTimeout(specialsTimeoutRef.current);
    }
    
    // Debounce the specials detection by 800ms to allow user to finish typing
    specialsTimeoutRef.current = setTimeout(() => {
      const detectedSpecials = [];
      
      items.forEach(item => {
        const previousCost = parseFloat(item.previous_cost) || 0;
        const newCostRaw = parseFloat(item.new_unit_cost) || 0;
        const qty = parseFloat(item.quantity_to_receive) || 0;
        
        // Get VAT rate for this item
        const vatRate = item.vat_category === "Zero-rated 0%" || item.vat_category === "Exempt" ? 0 : 0.15;
        
        // Convert new cost to excl VAT if costPricesIncludeVat is true
        // previous_cost is always stored excl VAT
        const newCostExclVat = costPricesIncludeVat && vatRate > 0 
          ? newCostRaw / (1 + vatRate) 
          : newCostRaw;
        
        // Only detect specials if the new cost looks like a complete number
        // (not just a partial input like "1" when typing "17")
        if (previousCost > 0 && newCostExclVat > 0 && newCostExclVat < previousCost) {
          const savingsPercent = ((previousCost - newCostExclVat) / previousCost) * 100;
          
          if (savingsPercent >= specialsThreshold) {
            detectedSpecials.push({
              product_id: item.product_id,
              plu_code: item.plu_code,
              name: item.name,
              previous_cost: previousCost,
              new_cost: newCostExclVat,
              savings_percent: savingsPercent.toFixed(1),
              savings_amount: (previousCost - newCostExclVat).toFixed(2),
              quantity: qty,
              detected_at: new Date().toISOString()
            });
          }
        }
      });
      
      // Check for new specials to show toast
      const prevIds = prevSpecialsRef.current.map(s => s.product_id);
      detectedSpecials.forEach(newSpecial => {
        if (!prevIds.includes(newSpecial.product_id)) {
          toast.success(
            `🎉 SPECIAL DETECTED! ${newSpecial.name} is ${newSpecial.savings_percent}% cheaper!`,
            { duration: 5000 }
          );
        }
      });
      
      prevSpecialsRef.current = detectedSpecials;
      setSpecials(detectedSpecials);
    }, 800); // Wait 800ms after last change before detecting specials
    
    // Cleanup timeout on unmount
    return () => {
      if (specialsTimeoutRef.current) {
        clearTimeout(specialsTimeoutRef.current);
      }
    };
  }, [items, specialsThreshold]);
  
  // Shelf Talker modal
  const [showShelfTalkerModal, setShowShelfTalkerModal] = useState(false);
  const [savedGrvItems, setSavedGrvItems] = useState([]);
  const [showPrintGrvPrompt, setShowPrintGrvPrompt] = useState(false);
  const [lastCreatedGrv, setLastCreatedGrv] = useState(null);
  
  // GRV History modal
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showCalculationAudit, setShowCalculationAudit] = useState(true);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Draft state
  const [hasDraft, setHasDraft] = useState(() => !!localStorage.getItem(GRV_DRAFT_KEY));
  const [showDraftBanner, setShowDraftBanner] = useState(() => !!localStorage.getItem(GRV_DRAFT_KEY));
  
  const productSearchRef = useRef(null);
  const supplierSearchRef = useRef(null);
  const poLookupRef = useRef(null);  // Track latest PO lookup to prevent race conditions

  const searchSuppliers = async (query) => {
    if (!query || query.length < 1) {
      setSupplierSearchResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/suppliers/search?q=${encodeURIComponent(query)}`);
      setSupplierSearchResults(response.data);
    } catch (error) {
      console.error("Error searching suppliers:", error);
    }
  };

  const handleSelectSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch(`${supplier.supplier_number} - ${supplier.name}`);
    setIsCod(supplier.is_cod || false);
    setShowSupplierSearch(false);
    setSupplierSearchResults([]);
  };

  // Auto-fill supplier when PO number is entered
  const handlePoNumberChange = async (value) => {
    const upperValue = value.toUpperCase();
    setPoNumber(upperValue);
    
    // Generate a unique lookup ID to track this specific request
    const lookupId = Date.now();
    poLookupRef.current = lookupId;
    
    // If PO number looks complete (e.g., "000001" or "000002"), try to fetch the PO
    if (upperValue && upperValue.length >= 4) {
      try {
        // Try to find the purchase order
        const response = await axios.get(`${API}/purchase-orders`);
        
        // Check if this is still the latest lookup (prevent race condition)
        if (poLookupRef.current !== lookupId) {
          console.log("Stale PO lookup ignored:", upperValue);
          return;
        }
        
        const orders = response.data || [];
        
        // Find matching PO by po_number
        const matchingPO = orders.find(po => 
          po.po_number === upperValue || 
          po.po_number === upperValue.replace('PO-', '') ||
          po.po_number?.padStart(6, '0') === upperValue ||
          po.po_number === upperValue.padStart(6, '0')
        );
        
        if (matchingPO && matchingPO.supplier_id) {
          // Fetch the supplier details
          try {
            const supplierRes = await axios.get(`${API}/suppliers/${matchingPO.supplier_id}`);
            
            // Check again if this is still the latest lookup (prevent race condition)
            if (poLookupRef.current !== lookupId) {
              console.log("Stale supplier fetch ignored for PO:", upperValue);
              return;
            }
            
            const supplier = supplierRes.data;
            if (supplier) {
              setSelectedSupplier(supplier);
              setSupplierSearch(`${supplier.supplier_number} - ${supplier.name}`);
              setIsCod(supplier.is_cod || false);
              toast.success(`Auto-filled supplier: ${supplier.name}`);
            }
          } catch (supplierError) {
            console.error("Error fetching supplier:", supplierError);
          }
        }
      } catch (error) {
        console.error("Error looking up PO:", error);
      }
    }
  };

  const handleValidateHeader = async () => {
    if (!selectedSupplier) {
      toast.error("Please select a supplier");
      return;
    }
    if (!supplierInvoiceNumber) {
      toast.error("Please enter supplier invoice number");
      return;
    }
    if (!invoiceAmount || parseFloat(invoiceAmount) <= 0) {
      toast.error("Please enter a valid invoice amount");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/grv/validate-header`, {
        po_number: poNumber || null,
        order_type: orderType,
        supplier_id: selectedSupplier.id,
        is_cod: isCod,
        received_date: receivedDate,
        supplier_invoice_number: supplierInvoiceNumber,
        invoice_amount_type: invoiceAmountType,
        invoice_amount: parseFloat(invoiceAmount),
        notes: notes
      });

      setHeaderValidation(response.data);
      
      // Show any warnings
      if (response.data.warnings && response.data.warnings.length > 0) {
        response.data.warnings.forEach(w => toast.warning(w));
      }
      
      // Set items from PO if available
      if (response.data.items && response.data.items.length > 0) {
        const initialItems = response.data.items.map(item => {
          const vatRate = getVatRateForItem(item);
          const baseCostExclVat = item.previous_cost || item.unit_cost || 0;
          const displayCost = costPricesIncludeVat && vatRate > 0
            ? baseCostExclVat * (1 + vatRate)
            : baseCostExclVat;

          return {
            ...item,
            quantity_to_receive: "",  // Leave empty
            new_unit_cost: displayCost > 0 ? displayCost.toFixed(2) : "",
            new_selling_price: item.price_locked ? item.frozen_price : null, // Frozen price shown as-is, else auto-calculated
            total_cost: 0,
            // Expiry tracking fields
            track_expiry: item.track_expiry || false,
            expiry_date: "",  // User enters this for tracked products
            batch_number: ""  // Optional batch/lot number
          };
        });
        setItems(initialItems);
        toast.success(`Loaded ${initialItems.length} items from PO ${poNumber}`);
      } else if (poNumber) {
        toast.info("No items found on PO. You can add items manually.");
        setItems([]);
      } else {
        setItems([]);
      }
      
      // Move to items screen
      setCurrentScreen("items");
      
    } catch (error) {
      console.error("Error validating header:", error);
      toast.error(error.response?.data?.detail || "Failed to validate GRV header");
    } finally {
      setLoading(false);
    }
  };

  // Open the full product creation modal
  const handleOpenNewProductModal = () => {
    // Fetch latest categories/departments
    fetchCategoriesDepartments();
    
    // Create a fresh product with supplier pre-filled
    const freshProduct = {
      ...defaultProduct,
      supplier: selectedSupplier?.name || "",
    };
    setNewProductForModal(freshProduct);
    setShowNewProductModal(true);
  };
  
  // Handle save from ProductDetailModal - create product and add to GRV
  const handleSaveNewProduct = async (productData) => {
    try {
      const response = await axios.post(`${API}/products`, productData);
      const createdProduct = response.data;
      
      toast.success(`Product "${createdProduct.name}" created successfully`);
      
      // Add to GRV items
      handleAddProduct(createdProduct);
      
      // Close modal
      setShowNewProductModal(false);
      setNewProductForModal(null);
      
    } catch (error) {
      console.error("Error creating product:", error);
      const errorMsg = error.response?.data?.detail || "Failed to create product";
      // Handle Pydantic validation errors which come as an array
      if (Array.isArray(errorMsg)) {
        const fieldErrors = errorMsg.map(e => e.msg || e.type).join(", ");
        toast.error(`Validation error: ${fieldErrors}`);
      } else {
        toast.error(errorMsg);
      }
    }
  };

  // Only the latest keystroke's results may land — slower earlier replies (e.g. "Ke") are dropped
  const searchSeqRef = useRef(0);
  const searchTimerRef = useRef(null);
  const searchProducts = (query) => {
    const seq = ++searchSeqRef.current;
    clearTimeout(searchTimerRef.current);
    if (!query || query.length < 2) {
      setProductSearchResults([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/products/search?q=${encodeURIComponent(query)}`);
        if (seq === searchSeqRef.current) setProductSearchResults(response.data.slice(0, 10));
      } catch (error) {
        console.error("Error searching products:", error);
      }
    }, 200);
  };


  const getVatRateForItem = (item) => {
    const vatCategory = item?.vat_category;
    return (vatCategory === "Zero-rated 0%" || vatCategory === "Exempt") ? 0 : 0.15;
  };

  const setCostDisplayMode = (includeVat) => {
    if (includeVat === costPricesIncludeVat) {
      setCostPricesIncludeVat(includeVat);
      return;
    }

    setItems((prevItems) => prevItems.map((item) => {
      if (item.new_unit_cost === "" || item.new_unit_cost === null || item.new_unit_cost === undefined) {
        return item;
      }

      const rawCost = parseFloat(item.new_unit_cost);
      if (Number.isNaN(rawCost)) {
        return item;
      }

      const vatRate = getVatRateForItem(item);
      const costExclVat = costPricesIncludeVat && vatRate > 0 ? rawCost / (1 + vatRate) : rawCost;
      const displayCost = includeVat && vatRate > 0 ? costExclVat * (1 + vatRate) : costExclVat;
      const qty = parseFloat(item.quantity_to_receive) || 0;
      const markup = Math.max(0, parseFloat(item.markup_percent) || 30);
      const calculatedSellingPrice = (costExclVat * (1 + markup / 100) * (1 + vatRate)).toFixed(2);

      return {
        ...item,
        new_unit_cost: displayCost.toFixed(2),
        total_cost: qty * displayCost,
        new_selling_price: (item.selling_price_manual || item.price_locked) ? item.new_selling_price : calculatedSellingPrice
      };
    }));

    setCostPricesIncludeVat(includeVat);
  };

  const handleAddProduct = async (product) => {
    // Check if product already in list
    if (items.some(item => item.product_id === product.id)) {
      toast.error("Product already in list");
      return;
    }
    if (selectedSupplier?.id && product.id) {
      try {
        const { data: chk } = await axios.get(`${API}/grv/supplier-product-check`, {
          params: { supplier_id: selectedSupplier.id, product_id: product.id },
        });
        if (!chk.on_list) {
          const msg = `${product.name} is NOT on ${chk.supplier_name}'s list` +
            (chk.product_supplier ? ` (its supplier is ${chk.product_supplier})` : "") +
            ".\n\nAre you sure you want to receive it on this GRV?";
          if (!window.confirm(msg)) return;
        }
      } catch (e) {
        console.warn("Supplier check failed", e);
      }
    }

    // Get current cost price (use last cost if available, otherwise average or legacy cost_price)
    const costExclVat = product.last_cost_price_excl_vat || product.average_cost_price_excl_vat || product.cost_price || 0;
    const markup = Math.max(0, product.markup_percent || 30);
    
    // Calculate default selling price including VAT
    // Formula: cost * (1 + markup%) * 1.15 (for standard VAT)
    const vatRate = product.vat_category === "Zero-rated 0%" || product.vat_category === "Exempt" ? 0 : 0.15;
    const calculatedSellingPrice = costExclVat > 0 
      ? (costExclVat * (1 + markup / 100) * (1 + vatRate)).toFixed(2)
      : "";

    // If cost prices should include VAT, convert the stored excl VAT cost to incl VAT for display
    const displayCost = costPricesIncludeVat && vatRate > 0 
      ? costExclVat * (1 + vatRate)  // Convert to incl VAT
      : costExclVat;

    const newItem = {
      product_id: product.id,
      plu_code: product.plu_code,
      barcode: product.barcode,
      name: product.name,
      quantity_ordered: 0,
      quantity_received: 0,
      quantity_to_receive: "",  // Leave empty for user to enter
      previous_cost: costExclVat,  // Always store excl VAT for comparison
      unit_cost: costExclVat,  // Store excl VAT
      new_unit_cost: displayCost > 0 ? displayCost.toFixed(2) : "",  // Show cost based on VAT setting
      current_stock: product.current_stock,
      markup_percent: markup,
      vat_category: product.vat_category || "Standard 15%",
      new_selling_price: product.price_locked ? (product.selling_price_incl_vat || calculatedSellingPrice) : calculatedSellingPrice,  // Show calculated price (preview only)
      price_locked: !!product.price_locked,
      frozen_price: product.selling_price_incl_vat || 0,
      selling_price_manual: false,  // Track if user manually changed the price
      total_cost: 0,  // Will be calculated when quantity is entered
      // Expiry tracking fields
      track_expiry: product.track_expiry || false,
      expiry_date: "",  // User enters this for tracked products
      batch_number: "",  // Optional batch/lot number
      // Shelf talker fields
      print_shelf_talker: true,  // Default to printing shelf talker
      print_stock_labels: product.item_price_sticker === true,  // Follow the product's "Print Item Price Stickers" flag — barcode presence no longer suppresses labels (user asked to allow printing regardless).
      stock_labels_qty: "",  // Custom override for label count; blank = use quantity_to_receive
      has_barcode: !!product.barcode,  // Track if product has barcode
      location: product.location || formatLocation(product.shelf_number, product.bin_number),
      shelf_number: product.shelf_number || "",
      bin_number: product.bin_number || "",
      selling_price: product.selling_price_incl_vat || product.selling_price || 0
    };

    setItems([...items, newItem]);
    setProductSearch("");
    setProductSearchResults([]);
    setShowProductSearch(false);
    toast.success(`Added ${product.name}`);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    const item = updatedItems[index];

    if (field === "markup_percent" && value !== "" && parseFloat(value) < 0) {
      toast.error("Markup % cannot be negative");
      return;
    }

    item[field] = value;
    
    // Helper function to calculate selling price from cost and markup
    const calculateSellingPrice = (cost, markup, vatCategory) => {
      const vatRate = vatCategory === "Zero-rated 0%" ? 0 : 0.15;
      // If cost includes VAT, extract the excl VAT cost first
      const costExclVat = costPricesIncludeVat && vatRate > 0 ? cost / (1 + vatRate) : cost;
      return (costExclVat * (1 + markup / 100) * (1 + vatRate)).toFixed(2);
    };
    
    // Helper function to calculate markup from selling price and cost
    const calculateMarkup = (sellingPrice, cost, vatCategory) => {
      if (cost <= 0) return 0;
      const vatRate = vatCategory === "Zero-rated 0%" ? 0 : 0.15;
      // If cost includes VAT, extract the excl VAT cost first
      const costExclVat = costPricesIncludeVat && vatRate > 0 ? cost / (1 + vatRate) : cost;
      const priceExclVat = sellingPrice / (1 + vatRate);
      const calculatedMarkup = ((priceExclVat / costExclVat - 1) * 100);
      return Math.max(0, calculatedMarkup).toFixed(1);
    };
    
    // Recalculate total cost when quantity or cost changes
    if (field === "quantity_to_receive" || field === "new_unit_cost") {
      const qty = parseFloat(item.quantity_to_receive) || 0;
      const cost = parseFloat(item.new_unit_cost) || 0;
      item.total_cost = qty * cost;

      // Recalculate selling price when cost changes (using current markup).
      // BUT — if the operator has already manually set the selling price for
      // this line (selling_price_manual === true), we must NOT overwrite it.
      // Their explicit override wins over any auto-recalc. Without this
      // guard, typing a New Price and then adjusting the cost afterwards
      // silently discards the New Price they entered.
      if (field === "new_unit_cost" && cost > 0 && !item.selling_price_manual && !item.price_locked) {
        const markup = Math.max(0, parseFloat(item.markup_percent) || 30);
        item.new_selling_price = calculateSellingPrice(cost, markup, item.vat_category);
      } else if (field === "new_unit_cost" && cost > 0 && (item.selling_price_manual || item.price_locked)) {
        // Cost changed but keep the operator's selling price — just refresh
        // the displayed markup so they see the new margin at the new cost.
        const sellingPrice = parseFloat(item.new_selling_price) || 0;
        if (sellingPrice > 0) {
          item.markup_percent = calculateMarkup(sellingPrice, cost, item.vat_category);
        }
      }
    }
    
    // When markup changes, recalculate selling price
    if (field === "markup_percent") {
      const cost = parseFloat(item.new_unit_cost) || 0;
      const markup = parseFloat(value) || 0;
      if (cost > 0 && markup >= 0) {
        item.new_selling_price = calculateSellingPrice(cost, markup, item.vat_category);
        // Flag as manual so the backend uses the displayed price instead of
        // falling back to the product's old stored markup_percent.
        item.selling_price_manual = true;
      }
    }
    
    // When selling price is manually changed, recalculate markup
    if (field === "new_selling_price") {
      item.selling_price_manual = true;  // User explicitly changed the price
      const cost = parseFloat(item.new_unit_cost) || 0;
      const sellingPrice = parseFloat(value) || 0;
      if (cost > 0 && sellingPrice > 0) {
        item.markup_percent = calculateMarkup(sellingPrice, cost, item.vat_category);
      }
    }

    // When VAT category changes, refresh selling price so the markup + shelf
    // price stay consistent with the new VAT treatment. Skipped if the
    // operator has manually set the price (their override wins).
    if (field === "vat_category") {
      const cost = parseFloat(item.new_unit_cost) || 0;
      const markup = Math.max(0, parseFloat(item.markup_percent) || 30);
      if (cost > 0 && !item.selling_price_manual && !item.price_locked) {
        item.new_selling_price = calculateSellingPrice(cost, markup, value);
      } else if (cost > 0 && item.selling_price_manual) {
        // Recompute displayed markup at the new VAT treatment but keep the price.
        const sellingPrice = parseFloat(item.new_selling_price) || 0;
        if (sellingPrice > 0) {
          item.markup_percent = calculateMarkup(sellingPrice, cost, value);
        }
      }
    }
    
    setItems(updatedItems);
  };

  const calculateTotals = () => {
    // Treat cost values as INCL VAT only when BOTH flags agree. The
    // costPricesIncludeVat checkbox is only meaningful in "incl" invoice
    // mode; if the invoice mode is "excl" the entered costs are always
    // excl VAT regardless of any stale checkbox state (previously this
    // desync caused Items Subtotal to be silently divided by 1.15,
    // yielding a spurious R = subtotal × 15% variance).
    const treatCostsAsIncl = costPricesIncludeVat && invoiceAmountType === "incl";

    const itemsSubtotalExcl = items.reduce((sum, item) => {
      const itemCost = item.total_cost || 0;
      if (treatCostsAsIncl) {
        const vatCategory = item.vat_category || 'Standard 15%';
        if (vatCategory === 'Zero-rated 0%' || vatCategory === 'Exempt') {
          return sum + itemCost;
        }
        return sum + (itemCost / 1.15);
      }
      return sum + itemCost;
    }, 0);

    const itemsVat = items.reduce((sum, item) => {
      const vatCategory = item.vat_category || 'Standard 15%';
      if (vatCategory === 'Zero-rated 0%' || vatCategory === 'Exempt') {
        return sum;
      }
      const itemCost = item.total_cost || 0;
      if (treatCostsAsIncl) {
        return sum + (itemCost - (itemCost / 1.15));
      }
      return sum + (itemCost * 0.15);
    }, 0);

    const deliveryDistribution = calculateDeliveryFeeDistribution();
    const deliveryFeeExclVat = deliveryDistribution.feeExclVat || 0;
    const deliveryFeeVat = deliveryFeeExclVat > 0 ? deliveryFeeExclVat * 0.15 : 0;

    const subtotal = itemsSubtotalExcl + (includeDeliveryFeeInTotals ? deliveryFeeExclVat : 0);
    const vat = itemsVat + (includeDeliveryFeeInTotals ? deliveryFeeVat : 0);
    const total = subtotal + vat;

    const invoiceValue = parseFloat(invoiceAmount) || 0;
    let variance;
    if (invoiceAmountType === "incl") {
      variance = total - invoiceValue;
    } else {
      variance = subtotal - invoiceValue;
    }

    return {
      itemSubtotal: itemsSubtotalExcl.toFixed(2),
      includeDeliveryFeeInTotals,
      deliveryFeeExclVat: deliveryFeeExclVat.toFixed(2),
      deliveryFeeVat: deliveryFeeVat.toFixed(2),
      subtotal: subtotal.toFixed(2),
      vat: vat.toFixed(2),
      total: total.toFixed(2),
      variance: variance.toFixed(2),
      isBalanced: Math.abs(variance) < 0.01
    };
  };

  const buildCalculationAudit = (deliveryDistribution, calculatedTotals) => {
    const validItems = items.filter((item) => (parseFloat(item.quantity_to_receive) || 0) > 0);

    const lineItems = validItems.map((item, index) => {
      const qty = parseFloat(item.quantity_to_receive) || 0;
      const rawCost = parseFloat(item.new_unit_cost) || 0;
      const vatRate = item.vat_category === "Zero-rated 0%" || item.vat_category === "Exempt" ? 0 : 0.15;
      // Costs are only treated as INCL VAT when the invoice mode itself
      // says "incl" — see calculateTotals for the same guard.
      const treatCostsAsIncl = costPricesIncludeVat && invoiceAmountType === "incl";
      const unitCostExclVat = treatCostsAsIncl && vatRate > 0 ? rawCost / (1 + vatRate) : rawCost;
      const lineSubtotalExclVat = qty * unitCostExclVat;
      const lineVat = lineSubtotalExclVat * vatRate;

      return {
        id: `${item.product_id}-${index}`,
        name: item.name,
        qty,
        vatRate,
        unitCostExclVat,
        lineSubtotalExclVat,
        lineVat,
      };
    });

    const itemsSubtotalExclVat = lineItems.reduce((sum, line) => sum + line.lineSubtotalExclVat, 0);
    const itemsVat = lineItems.reduce((sum, line) => sum + line.lineVat, 0);
    const deliveryFeeExclVat = deliveryDistribution?.feeExclVat || 0;
    const deliveryFeeVat = deliveryFeeExclVat > 0 ? deliveryFeeExclVat * 0.15 : 0;

    const subtotalExclVat = itemsSubtotalExclVat + (includeDeliveryFeeInTotals ? deliveryFeeExclVat : 0);
    const vatTotal = itemsVat + (includeDeliveryFeeInTotals ? deliveryFeeVat : 0);
    const totalInclVat = subtotalExclVat + vatTotal;

    const invoiceValue = parseFloat(invoiceAmount) || 0;
    const comparisonBasis = invoiceAmountType === "incl" ? "Total (Incl VAT)" : "Subtotal (Excl VAT)";
    const comparisonAmount = invoiceAmountType === "incl" ? totalInclVat : subtotalExclVat;
    const variance = comparisonAmount - invoiceValue;
    const tolerance = 0.01;

    return {
      lineItems,
      itemsSubtotalExclVat,
      itemsVat,
      deliveryFeeExclVat,
      deliveryFeeVat,
      subtotalExclVat,
      vatTotal,
      totalInclVat,
      comparisonBasis,
      comparisonAmount,
      variance,
      isBalanced: Math.abs(variance) <= tolerance,
      subtotalCheckPass: Math.abs(subtotalExclVat - (parseFloat(calculatedTotals?.subtotal) || 0)) <= tolerance,
      vatCheckPass: Math.abs(vatTotal - (parseFloat(calculatedTotals?.vat) || 0)) <= tolerance,
      totalCheckPass: Math.abs(totalInclVat - (parseFloat(calculatedTotals?.total) || 0)) <= tolerance,
    };
  };

  // Calculate delivery fee distribution across items
  const calculateDeliveryFeeDistribution = () => {
    const fee = parseFloat(deliveryFee) || 0;
    if (fee <= 0) return { items: [], totalFee: 0, feeExclVat: 0 };
    
    // Convert fee to excl VAT if it includes VAT
    const feeExclVat = deliveryFeeIncludesVat ? fee / 1.15 : fee;
    
    // Calculate total value of all items (excl VAT) for proportional distribution
    const totalValue = items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity_to_receive) || 0;
      const cost = parseFloat(item.new_unit_cost) || 0;
      if (qty <= 0) return sum;
      
      // Convert to excl VAT if needed
      const vatRate = item.vat_category === "Zero-rated 0%" || item.vat_category === "Exempt" ? 0 : 0.15;
      const costExclVat = costPricesIncludeVat && vatRate > 0 ? cost / (1 + vatRate) : cost;
      return sum + (qty * costExclVat);
    }, 0);
    
    if (totalValue <= 0) return { items: [], totalFee: fee, feeExclVat };
    
    // Distribute fee proportionally
    const distribution = items.map(item => {
      const qty = parseFloat(item.quantity_to_receive) || 0;
      const cost = parseFloat(item.new_unit_cost) || 0;
      if (qty <= 0) return null;
      
      const vatRate = item.vat_category === "Zero-rated 0%" || item.vat_category === "Exempt" ? 0 : 0.15;
      const costExclVat = costPricesIncludeVat && vatRate > 0 ? cost / (1 + vatRate) : cost;
      const itemValue = qty * costExclVat;
      
      // Calculate proportion of delivery fee for this item
      const proportion = itemValue / totalValue;
      const itemDeliveryFee = feeExclVat * proportion;
      const feePerUnit = itemDeliveryFee / qty;
      
      return {
        product_id: item.product_id,
        fee_key: item.direct_line_id || item.product_id,
        name: item.name,
        plu_code: item.plu_code,
        qty,
        original_cost: costExclVat,
        delivery_fee_total: itemDeliveryFee,
        delivery_fee_per_unit: feePerUnit,
        landed_cost: costExclVat + feePerUnit,
        proportion_percent: (proportion * 100).toFixed(1)
      };
    }).filter(Boolean);
    
    return { items: distribution, totalFee: fee, feeExclVat };
  };

  const checkPriceIncreases = () => {
    const alerts = items.filter(item => {
      const newCostRaw = parseFloat(item.new_unit_cost) || 0;
      const vatRate = item.vat_category === "Zero-rated 0%" || item.vat_category === "Exempt" ? 0 : 0.15;
      
      // Convert to excl VAT if costPricesIncludeVat is true
      const newCostExclVat = costPricesIncludeVat && vatRate > 0 
        ? newCostRaw / (1 + vatRate) 
        : newCostRaw;
      const prevCost = item.previous_cost || 0;
      
      return newCostExclVat > prevCost && prevCost > 0;
    }).map(item => {
      const newCostRaw = parseFloat(item.new_unit_cost) || 0;
      const vatRate = item.vat_category === "Zero-rated 0%" || item.vat_category === "Exempt" ? 0 : 0.15;
      const newCostExclVat = costPricesIncludeVat && vatRate > 0 
        ? newCostRaw / (1 + vatRate) 
        : newCostRaw;
      
      return {
        name: item.name,
        plu_code: item.plu_code,
        previous_cost: item.previous_cost,
        new_cost: newCostExclVat,
        change_percent: ((newCostExclVat - item.previous_cost) / item.previous_cost * 100).toFixed(1)
      };
    });
    
    return alerts;
  };

  const handleSubmitGRV = async () => {
    // Validate items
    const validItems = items.filter(item => (parseFloat(item.quantity_to_receive) || 0) > 0);
    if (validItems.length === 0) {
      toast.error("Please add at least one item to receive");
      return;
    }

    // Check for missing expiry dates on tracked products
    const missingExpiry = validItems.filter(item => item.track_expiry && !item.expiry_date);
    if (missingExpiry.length > 0) {
      toast.error(`Please enter expiry dates for: ${missingExpiry.map(i => i.name).join(", ")}`);
      return;
    }

    // Check for price increases and show alert
    const alerts = checkPriceIncreases();
    if (alerts.length > 0) {
      setPriceAlerts(alerts);
      setShowPriceAlerts(true);
      return;
    }

    await submitGRV();
  };

  const submitGRV = async () => {
    const frozenChanges = items.filter((it) => it.price_locked && it.selling_price_manual
      && (parseFloat(it.quantity_to_receive) || 0) > 0
      && Math.abs((parseFloat(it.new_selling_price) || 0) - (parseFloat(it.frozen_price) || 0)) > 0.004);
    let overrideFrozen = false;
    if (frozenChanges.length > 0) {
      overrideFrozen = window.confirm(
        "These products have a FROZEN selling price:\n\n" +
        frozenChanges.map((it) => `${it.name}: R${Number(it.frozen_price).toFixed(2)} -> R${Number(it.new_selling_price).toFixed(2)}`).join("\n") +
        "\n\nOK = change to your new price.  Cancel = keep the frozen price."
      );
    }
    setSubmitting(true);
    try {
      const totals = calculateTotals();
      
      // Calculate delivery fee distribution if a fee was entered
      const deliveryDistribution = calculateDeliveryFeeDistribution();
      const hasDeliveryFee = deliveryDistribution.feeExclVat > 0;
      
      // Create a map of product_id -> delivery fee data for quick lookup
      const deliveryFeeMap = {};
      if (hasDeliveryFee) {
        deliveryDistribution.items.forEach(dist => {
          deliveryFeeMap[dist.fee_key] = {
            landed_cost: dist.landed_cost,
            fee_per_unit: dist.delivery_fee_per_unit
          };
        });
      }
      
      const grvData = {
        po_id: headerValidation?.po_details?.id || null,
        po_number: poNumber || null,
        order_type: orderType,
        supplier_id: selectedSupplier?.id,
        is_cod: isCod,
        received_date: receivedDate,
        supplier_invoice_number: supplierInvoiceNumber,
        invoice_amount_type: invoiceAmountType,
        invoice_amount: parseFloat(invoiceAmount),
        delivery_fee: parseFloat(deliveryFee) || null,
        delivery_fee_excl_vat: deliveryDistribution.feeExclVat || null,
        include_delivery_fee_in_totals: includeDeliveryFeeInTotals,
        cost_prices_include_vat: costPricesIncludeVat,
        items: items.filter(item => (parseFloat(item.quantity_to_receive) || 0) > 0).map(item => {
          const qty = parseFloat(item.quantity_to_receive) || 0;
          const rawCost = parseFloat(item.new_unit_cost) || 0;
          
          // Convert cost to excl VAT if needed
          const vatRate = item.vat_category === "Zero-rated 0%" || item.vat_category === "Exempt" ? 0 : 0.15;
          const costExclVat = costPricesIncludeVat && vatRate > 0 
            ? rawCost / (1 + vatRate) 
            : rawCost;
          
          // Get delivery fee data for this product
          const deliveryData = deliveryFeeMap[item.direct_line_id || item.product_id];
          
          return {
            product_id: item.product_id,
            plu_code: item.plu_code,
            barcode: item.barcode,
            name: item.name,
            quantity_ordered: item.quantity_ordered || 0,
            quantity_received: qty,
            previous_cost: item.previous_cost || 0,
            unit_cost: costExclVat,  // Always send excl VAT
            total_cost: qty * costExclVat,
            new_selling_price: item.selling_price_manual ? parseFloat(item.new_selling_price) : null,
            override_price_lock: !!(item.price_locked && overrideFrozen),
            expiry_date: item.expiry_date || null,
            batch_number: item.batch_number || null,
            // Delivery fee fields
            landed_cost_per_unit: deliveryData ? deliveryData.landed_cost : null,
            delivery_fee_per_unit: deliveryData ? deliveryData.fee_per_unit : null,
            // Direct-order once-off items (no Stock Master row)
            is_once_off: item.is_once_off === true || String(item.product_id || "").startsWith("once-off-"),
            ...(item.is_direct_line ? {
              is_direct_line: true,
              direct_line_id: item.direct_line_id,
              direct_code_name: item.direct_code_name,
              co_ticket_id: item.co_ticket_id,
              co_ticket_no: item.co_ticket_no,
              co_customer_name: item.co_customer_name,
            } : {}),
            // Custom override for number of stock labels to print (blank/0 = use qty_received)
            stock_labels_qty: (() => {
              const v = parseFloat(item.stock_labels_qty);
              return Number.isFinite(v) && v >= 0 ? v : null;
            })(),
            print_stock_labels: !!item.print_stock_labels && !item.has_barcode
          };
        }),
        subtotal: parseFloat(totals.subtotal),
        vat_amount: parseFloat(totals.vat),
        total: parseFloat(totals.total),
        notes: notes,
        received_by: "Cashier 1"
      };

      const response = await saveWithRetry(() => axios.post(`${API}/grv`, grvData));

      // Store GRV for print prompt
      setLastCreatedGrv(response.data);

      // Show success message with delivery fee info if applicable
      if (hasDeliveryFee) {
        toast.success(`GRV ${response.data.grv_number} created! Delivery fee of R${deliveryDistribution.feeExclVat.toFixed(2)} distributed across ${deliveryDistribution.items.length} items.`);
      } else {
        toast.success(`GRV ${response.data.grv_number} created successfully!`);
      }

      // Warn user about lines that couldn't be matched to Stock Master and
      // were recorded as once-off so the GRV could still complete.
      const demoted = response.data.auto_demoted_to_once_off || [];
      if (demoted.length > 0) {
        const names = demoted.map((d) => d.name).filter(Boolean).slice(0, 3).join(", ");
        const more = demoted.length > 3 ? ` (+${demoted.length - 3} more)` : "";
        toast.warning(
          `${demoted.length} line${demoted.length === 1 ? "" : "s"} couldn't be linked to Stock Master and were saved as once-off: ${names}${more}. Open this GRV in History → click "Convert to Stock Master" on each to bring them into stock.`,
          { duration: 12000 }
        );
      }
      
      // Save items for shelf talker printing (only items marked for printing)
      // Build a map of product_id -> backend-calculated selling price (includes delivery fee)
      const backendPriceMap = {};
      if (response.data.items && response.data.items.length > 0) {
        response.data.items.forEach(bi => {
          if (bi.new_selling_price) {
            backendPriceMap[bi.product_id] = bi.new_selling_price;
          }
        });
      }

      const itemsForShelfTalker = items
        .filter(item => (parseFloat(item.quantity_to_receive) || 0) > 0 && item.print_shelf_talker)
        .map(item => ({
          product_id: item.product_id,
          plu_code: item.plu_code,
          barcode: item.barcode,
          name: item.name,
          // Use backend-calculated price (includes delivery fee in landed cost) as the authoritative price
          selling_price: backendPriceMap[item.product_id] || parseFloat(item.new_selling_price) || item.selling_price || 0,
          shelf_number: item.shelf_number,
          bin_number: item.bin_number,
          location: item.location,
          print_shelf_talker: item.print_shelf_talker
        }));
      
      // Show shelf talker modal if there are items to print
      if (itemsForShelfTalker.length > 0) {
        setSavedGrvItems(itemsForShelfTalker);
        setShowShelfTalkerModal(true);
      }
      
      // Show print GRV prompt
      setShowPrintGrvPrompt(true);
      
      // Reset form
      resetForm();
      
    } catch (error) {
      console.error("Error creating GRV:", error);
      toast.error(errorMessage(error, "Failed to create GRV"));
    } finally {
      setSubmitting(false);
      setShowPriceAlerts(false);
    }
  };

  // ── Draft helpers ──────────────────────────────────────────────────────────
  const saveDraft = () => {
    const draft = {
      savedAt: new Date().toISOString(),
      currentScreen,
      orderType,
      poNumber,
      selectedSupplier,
      isCod,
      receivedDate,
      supplierInvoiceNumber,
      invoiceAmountType,
      invoiceAmount,
      costPricesIncludeVat,
      deliveryFee,
      deliveryFeeIncludesVat,
      includeDeliveryFeeInTotals,
      notes,
      items,
    };
    localStorage.setItem(GRV_DRAFT_KEY, JSON.stringify(draft));
    setHasDraft(true);
    setShowDraftBanner(false);
    toast.success("GRV draft saved — you can safely navigate away and resume later.");
  };

  const loadDraft = () => {
    try {
      const raw = localStorage.getItem(GRV_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      setCurrentScreen(draft.currentScreen || "header");
      setOrderType(draft.orderType ?? 1);
      setPoNumber(draft.poNumber || "");
      setSelectedSupplier(draft.selectedSupplier || null);
      setSupplierSearch(draft.selectedSupplier?.name || "");
      setIsCod(draft.isCod || false);
      setReceivedDate(draft.receivedDate || new Date().toISOString().split("T")[0]);
      setSupplierInvoiceNumber(draft.supplierInvoiceNumber || "");
      setInvoiceAmountType(draft.invoiceAmountType || "excl");
      setInvoiceAmount(draft.invoiceAmount || "");
      // Ensure checkbox syncs with invoice type: Excl VAT = unchecked
      const draftType = draft.invoiceAmountType || "excl";
      setCostPricesIncludeVat(draftType === "incl" ? (draft.costPricesIncludeVat !== false) : false);
      setDeliveryFee(draft.deliveryFee || "");
      setDeliveryFeeIncludesVat(draft.deliveryFeeIncludesVat || false);
      setIncludeDeliveryFeeInTotals(draft.includeDeliveryFeeInTotals || false);
      setNotes(draft.notes || "");
      setItems(draft.items || []);
      setShowDraftBanner(false);
      const savedAt = new Date(draft.savedAt).toLocaleString();
      toast.success(`Draft restored (saved ${savedAt})`);
    } catch (e) {
      toast.error("Could not load draft");
    }
  };

  const discardDraft = () => {
    localStorage.removeItem(GRV_DRAFT_KEY);
    setHasDraft(false);
    setShowDraftBanner(false);
    toast.info("Draft discarded");
  };
  // ────────────────────────────────────────────────────────────────────────────

  const resetForm = () => {
    localStorage.removeItem(GRV_DRAFT_KEY);
    setHasDraft(false);
    setShowDraftBanner(false);
    setCurrentScreen("header");
    setOrderType(1);
    setPoNumber("");
    setSupplierSearch("");
    setSelectedSupplier(null);
    setIsCod(false);
    setReceivedDate(new Date().toISOString().split("T")[0]);
    setSupplierInvoiceNumber("");
    setInvoiceAmountType("excl");
    setInvoiceAmount("");
    setDeliveryFee("");
    setDeliveryFeeIncludesVat(false);
    setIncludeDeliveryFeeInTotals(false);
    setNotes("");
    setHeaderValidation(null);
    setItems([]);
    setPriceAlerts([]);
    setSpecials([]);
  };

  // ── Stock label printing (one label per unit received per GRV item) ──────
  // Delegated to a shared util so the GRV History modal can re-print labels too.
  const printStockLabels = (grv) => printStockLabelsForGRV(grv, toast);

  // Send stock labels to the in-app Print Station queue (auto-fires at store).
  const sendStockLabelsToStorePrinter = async (grv) => {
    const html = await buildStockLabelsHtmlForGRV(grv);
    if (!html) {
      toast.warning("No stock labels to print for this GRV.");
      return;
    }
    try {
      await submitPrintJob({
        jobType: "stock_labels",
        title: `Stock Labels — ${grv.grv_number}`,
        html,
        pageSizeHint: "40mm 30mm",
        sourceType: "grv",
        sourceId: grv.id || null,
        sourceNumber: grv.grv_number || null,
      });
      toast.success(`Stock labels for ${grv.grv_number} queued for the store printer.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to queue print job");
    }
  };

  const handlePrintGrv = async (grv) => {
    if (!grv) return;
    // Fetch full details if items not included
    let fullGrv = grv;
    if (!grv.items || grv.items.length === 0) {
      try {
        const res = await axios.get(`${API}/grv/${grv.id}`);
        fullGrv = res.data;
      } catch { return; }
    }
    // Delivery-fee derivations for the printable HTML. These MUST live
    // outside the fetch's try/catch so they're always defined regardless of
    // whether the fetch ran (prev bug: consts nested in try → ReferenceError
    // whenever the GRV already had items inline).
    const includeDeliveryInTotals = fullGrv.include_delivery_fee_in_totals !== false;
    const deliveryFeeExclVat = fullGrv.delivery_fee_excl_vat || 0;
    const deliveryFeeVat = fullGrv.delivery_fee_vat ?? (deliveryFeeExclVat > 0 ? deliveryFeeExclVat * 0.15 : 0);
    const itemsSubtotal = includeDeliveryInTotals ? (fullGrv.subtotal || 0) - deliveryFeeExclVat : (fullGrv.subtotal || 0);
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';
    const fmtCur = (a) => `R ${(a || 0).toFixed(2)}`;
    const rows = (fullGrv.items || []).map((item, i) => `
      <tr>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:center">${i + 1}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee">${item.name}<br/><span style="font-size:10px;color:#888">PLU: ${item.plu_code || ''}</span></td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:center">${item.quantity_received || item.quantity_to_receive || 0}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right">${fmtCur(item.unit_cost)}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right">${item.new_selling_price ? fmtCur(item.new_selling_price) : '-'}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right;font-weight:600">${fmtCur(item.total_cost)}</td>
      </tr>`).join('');
    const html = `<!DOCTYPE html><html><head><title>GRV ${fullGrv.grv_number}</title>
      <style>body{font-family:Arial,sans-serif;margin:20px;font-size:12px}h1{font-size:18px;margin:0 0 4px}table{width:100%;border-collapse:collapse;margin-top:12px}th{background:#f3f4f6;padding:6px 8px;text-align:left;font-size:11px;border-bottom:2px solid #d1d5db}.ig{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:8px;margin-bottom:8px}.ib{border:1px solid #e5e7eb;padding:6px 8px;border-radius:4px}.il{font-size:9px;color:#6b7280;text-transform:uppercase}.iv{font-weight:600;font-size:13px}.t{text-align:right;margin-top:12px}.t td{padding:3px 8px}.tr{font-size:16px;font-weight:bold;border-top:2px solid #111}@media print{body{margin:10px}}</style></head><body>
      <h1>GOODS RECEIVED VOUCHER</h1>
      <div class="ig">
        <div class="ib"><div class="il">GRV Number</div><div class="iv" style="color:#2563eb">${fullGrv.grv_number}</div></div>
        <div class="ib"><div class="il">Date</div><div class="iv">${fmtDate(fullGrv.received_date)}</div></div>
        <div class="ib"><div class="il">Supplier</div><div class="iv">${fullGrv.supplier_name || '-'}</div></div>
        <div class="ib"><div class="il">Invoice #</div><div class="iv">${fullGrv.supplier_invoice_number || '-'}</div></div>
      </div>
      ${fullGrv.po_number ? `<div style="margin-bottom:8px;font-size:11px">PO Reference: <strong>${fullGrv.po_number}</strong></div>` : ''}
      <table><thead><tr>
        <th style="width:30px;text-align:center">#</th><th>Product</th><th style="text-align:center">Qty</th>
        <th style="text-align:right">Unit Cost</th><th style="text-align:right">New Price</th><th style="text-align:right">Total</th>
      </tr></thead><tbody>${rows}</tbody></table>
      <table class="t" style="width:320px;margin-left:auto">
        <tr><td style="color:#6b7280">Items Subtotal (Excl VAT):</td><td>${fmtCur(itemsSubtotal)}</td></tr>
        ${deliveryFeeExclVat > 0 ? `<tr><td style="color:#6b7280">Delivery Fee (Excl VAT)${includeDeliveryInTotals ? '' : ' - excluded from total'}:</td><td>${fmtCur(deliveryFeeExclVat)}</td></tr>` : ''}
        <tr><td style="color:#6b7280">Subtotal (Excl VAT):</td><td>${fmtCur(fullGrv.subtotal)}</td></tr>
        <tr><td style="color:#6b7280">VAT (${includeDeliveryInTotals ? 'on applicable items + delivery' : 'on applicable items only'}):</td><td>${fmtCur(fullGrv.vat_amount)}</td></tr>
        ${!includeDeliveryInTotals && deliveryFeeExclVat > 0 ? `<tr><td style="color:#6b7280">Delivery VAT (excluded):</td><td>${fmtCur(deliveryFeeVat)}</td></tr>` : ''}
        <tr class="tr"><td>Total:</td><td style="color:#2563eb">${fmtCur(fullGrv.total)}</td></tr>
      </table>
      ${fullGrv.notes ? `<div style="margin-top:16px;padding:8px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:4px;font-size:11px"><strong>Notes:</strong> ${fullGrv.notes}</div>` : ''}
      </body></html>`;
    const w = window.open('', '_blank', 'width=800,height=600');
    w.document.write(html);
    w.document.close();
    w.onload = () => { w.print(); };
  };

  const totals = calculateTotals();
  const deliveryDistribution = calculateDeliveryFeeDistribution();
  const calculationAudit = buildCalculationAudit(deliveryDistribution, totals);

  // Function to distribute small variance (< R5) evenly across products
  const distributeVariance = () => {
    const variance = parseFloat(totals.variance);
    if (Math.abs(variance) < 0.01 || Math.abs(variance) >= 5) {
      return; // Only distribute if variance is between R0.01 and R4.99
    }

    // Get items with quantity and cost
    const itemsWithCost = items.filter(item => {
      const qty = parseFloat(item.quantity_to_receive) || 0;
      const cost = parseFloat(item.new_unit_cost) || 0;
      return qty > 0 && cost > 0;
    });

    if (itemsWithCost.length === 0) {
      toast.error("No items with valid quantities and costs to adjust");
      return;
    }

    // Calculate total cost across all items (for proportional distribution)
    const totalItemsCost = itemsWithCost.reduce((sum, item) => {
      const qty = parseFloat(item.quantity_to_receive) || 0;
      const cost = parseFloat(item.new_unit_cost) || 0;
      return sum + (qty * cost);
    }, 0);

    if (totalItemsCost <= 0) {
      toast.error("Total cost is zero, cannot distribute variance");
      return;
    }

    // Variance is (our_total - invoice_total)
    // If variance is negative, our total is LESS than invoice - we need to INCREASE costs
    // If variance is positive, our total is MORE than invoice - we need to DECREASE costs
    // So we need to SUBTRACT the variance from costs (not add)
    const adjustmentNeeded = -variance; // Negate to get the correct direction

    // Calculate adjustment per unit for each item (proportional to their total cost)
    const updatedItems = items.map(item => {
      const qty = parseFloat(item.quantity_to_receive) || 0;
      const cost = parseFloat(item.new_unit_cost) || 0;
      
      if (qty <= 0 || cost <= 0) {
        return item; // Skip items without quantity or cost
      }

      // Calculate this item's proportion of the total cost
      const itemTotalCost = qty * cost;
      const proportion = itemTotalCost / totalItemsCost;
      
      // Calculate the adjustment for this item (proportional to its share)
      const itemAdjustmentShare = adjustmentNeeded * proportion;
      
      // Adjust the unit cost (divide adjustment by quantity)
      const costAdjustment = itemAdjustmentShare / qty;
      const newCost = cost + costAdjustment;
      
      // Recalculate selling price with the new cost
      const vatCategory = item.vat_category || 'Standard 15%';
      const vatRate = vatCategory === "Zero-rated 0%" ? 0 : 0.15;
      const markup = Math.max(0, parseFloat(item.markup_percent) || 30);
      const costExclVat = costPricesIncludeVat && vatRate > 0 ? newCost / (1 + vatRate) : newCost;
      const newSellingPrice = (costExclVat * (1 + markup / 100) * (1 + vatRate)).toFixed(2);

      return {
        ...item,
        new_unit_cost: parseFloat(newCost.toFixed(4)), // Keep 4 decimals for precision
        new_selling_price: newSellingPrice,
        total_cost: qty * parseFloat(newCost.toFixed(4))
      };
    });

    setItems(updatedItems);
    toast.success(`Variance of R ${Math.abs(variance).toFixed(2)} distributed across ${itemsWithCost.length} item(s)`);
  };

  // Check if variance can be distributed (between R0.01 and R4.99)
  const canDistributeVariance = () => {
    const variance = parseFloat(totals.variance);
    return Math.abs(variance) >= 0.01 && Math.abs(variance) < 5;
  };

  return (
    <div className="p-6 w-full">

      {/* Draft Resume Banner */}
      {showDraftBanner && (
        <div className="mb-4 flex items-center justify-between bg-amber-50 border border-amber-300 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2 text-amber-800">
            <FolderOpen className="h-5 w-5 text-amber-600" />
            <span className="font-medium text-sm">You have an unsaved GRV draft.</span>
            <span className="text-xs text-amber-600 ml-1">
              {(() => { try { const d = JSON.parse(localStorage.getItem(GRV_DRAFT_KEY)); return d?.savedAt ? `Saved ${new Date(d.savedAt).toLocaleString()}` : ""; } catch { return ""; } })()}
            </span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={loadDraft} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="resume-draft-btn">
              <FolderOpen className="h-4 w-4 mr-1" /> Resume Draft
            </Button>
            <Button size="sm" variant="outline" onClick={discardDraft} className="border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="discard-draft-btn">
              Discard
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Truck className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold">Stock Receiving (GRV)</h1>
            <p className="text-gray-500">Receive stock and update inventory</p>
          </div>
        </div>
        <div className="flex gap-2">
          {/* Specials Tab Button */}
          <Button 
            variant={showSpecialsTab ? "default" : "outline"}
            onClick={() => setShowSpecialsTab(!showSpecialsTab)}
            className={showSpecialsTab ? "bg-yellow-500 hover:bg-yellow-600" : specials.length > 0 ? "border-yellow-500 text-yellow-700 animate-pulse" : ""}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Specials {specials.length > 0 && `(${specials.length})`}
          </Button>
          <Button variant="outline" onClick={() => setShowHistoryModal(true)}>
            <History className="h-4 w-4 mr-2" />
            GRV History
          </Button>
          <Button
            variant="outline"
            onClick={() => setAiImportOpen(true)}
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            data-testid="ai-import-invoice-btn"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Import from Invoice (AI)
          </Button>
          {(currentScreen === "items" || (currentScreen === "header" && (selectedSupplier || items.length > 0))) && (
            <Button variant="outline" onClick={saveDraft} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="save-draft-btn">
              <Save className="h-4 w-4 mr-2" />
              Save Draft
            </Button>
          )}
          {currentScreen === "items" && (
            <Button
              variant="outline"
              onClick={() => {
                const hasItems = items.some((it) => parseFloat(it.quantity_to_receive) > 0);
                const hasHeader = poNumber || supplierInvoiceNumber || parseFloat(invoiceAmount) > 0;
                if (hasItems || hasHeader) {
                  if (!window.confirm(
                    "Start a completely new GRV?\n\nThis discards the current draft, all items and header details.\n\nIf you just want to edit the header, use the small Back button next to the invoice summary instead."
                  )) return;
                }
                resetForm();
              }}
              data-testid="grv-start-new-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Start New GRV
            </Button>
          )}
        </div>
      </div>

      {/* Specials Panel */}
      {showSpecialsTab && (
        <Card className="mb-6 border-yellow-300 bg-yellow-50">
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2 text-yellow-800">
                <Sparkles className="h-5 w-5" />
                Specials Detected - Items Cheaper Than Normal
              </CardTitle>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-yellow-700">Alert threshold:</Label>
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={specialsThreshold}
                  onChange={(e) => setSpecialsThreshold(parseInt(e.target.value) || 10)}
                  className="w-20 h-8 text-center"
                />
                <span className="text-sm text-yellow-700">% cheaper</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {specials.length === 0 ? (
              <p className="text-center text-yellow-700 py-4">
                No specials detected yet. Items will appear here when a new cost price is {specialsThreshold}% or more cheaper than the previous cost.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-yellow-100">
                    <TableHead>PLU</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Previous Cost</TableHead>
                    <TableHead className="text-right">New Cost</TableHead>
                    <TableHead className="text-right">Savings</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Total Savings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {specials.map((special, idx) => (
                    <TableRow key={idx} className="bg-yellow-50">
                      <TableCell className="font-mono">{special.plu_code}</TableCell>
                      <TableCell className="font-medium">{special.name}</TableCell>
                      <TableCell className="text-right line-through text-gray-500">
                        R {special.previous_cost.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-bold text-green-600">
                        R {special.new_cost.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm font-bold">
                          -{special.savings_percent}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{special.quantity}</TableCell>
                      <TableCell className="text-right font-bold text-green-600">
                        R {(parseFloat(special.savings_amount) * special.quantity).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {specials.length > 0 && (
              <div className="mt-4 flex justify-between items-center bg-green-100 p-3 rounded-lg">
                <span className="text-green-800 font-medium">
                  🎉 Total Savings on this GRV:
                </span>
                <span className="text-2xl font-bold text-green-700">
                  R {specials.reduce((sum, s) => sum + (parseFloat(s.savings_amount) * s.quantity), 0).toFixed(2)}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Screen 1: Header Information */}
      {currentScreen === "header" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              GRV Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Order Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-base font-semibold">Order Type *</Label>
                <Select value={orderType.toString()} onValueChange={(v) => setOrderType(parseInt(v))}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...ORDER_TYPES].sort((a, b) => (a.label || '').localeCompare(b.label || '')).map(type => (
                      <SelectItem key={type.value} value={type.value.toString()}>
                        <div>
                          <div className="font-medium">{type.label}</div>
                          <div className="text-xs text-gray-500">{type.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-base font-semibold">PO Number (Optional)</Label>
                <Input
                  value={poNumber}
                  onChange={(e) => handlePoNumberChange(e.target.value)}
                  placeholder="e.g., 000001"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">Enter PO number to auto-fill supplier</p>
              </div>
            </div>

            {/* Supplier */}
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <Label className="text-base font-semibold">Supplier *</Label>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <Input
                    ref={supplierSearchRef}
                    value={supplierSearch}
                    onChange={(e) => {
                      setSupplierSearch(e.target.value);
                      searchSuppliers(e.target.value);
                      if (!e.target.value) {
                        setSelectedSupplier(null);
                      }
                    }}
                    onFocus={() => setShowSupplierSearch(true)}
                    placeholder="Type supplier number or name..."
                    className="pl-10"
                  />
                  {showSupplierSearch && supplierSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {supplierSearchResults.map(supplier => (
                        <div
                          key={supplier.id}
                          className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => handleSelectSupplier(supplier)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-semibold">{supplier.supplier_number}</span>
                            <span>{supplier.name}</span>
                            {supplier.is_cod && (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-1 rounded">COD</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">{supplier.payment_terms}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedSupplier && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Selected: {selectedSupplier.name}
                  </p>
                )}
              </div>
              <div className="flex items-end gap-4 pb-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_cod"
                    checked={isCod}
                    onCheckedChange={setIsCod}
                  />
                  <Label htmlFor="is_cod" className="font-normal">COD Supplier</Label>
                </div>
              </div>
            </div>

            {/* Date and Invoice */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-base font-semibold">Date Received *</Label>
                <Input
                  type="date"
                  value={receivedDate}
                  onChange={(e) => setReceivedDate(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-base font-semibold">Supplier Invoice # *</Label>
                <Input
                  value={supplierInvoiceNumber}
                  onChange={(e) => setSupplierInvoiceNumber(e.target.value)}
                  placeholder="Enter invoice number"
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-base font-semibold">Invoice Amount *</Label>
                <div className="flex gap-2 mt-2">
                  <Select value={invoiceAmountType} onValueChange={(value) => {
                    setInvoiceAmountType(value);
                    // Auto-sync display mode with invoice amount type
                    setCostDisplayMode(value === "incl");
                  }}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excl">Excl VAT</SelectItem>
                      <SelectItem value="incl">Incl VAT</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-2.5 text-gray-500">R</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={invoiceAmount}
                      onChange={(e) => setInvoiceAmount(e.target.value)}
                      placeholder=""
                      className="pl-8"
                    />
                  </div>
                </div>
                {/* Cost prices VAT toggle - auto-synced with invoice type but can be overridden */}
                <div className="flex items-center gap-2 mt-3 p-2 bg-blue-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="costPricesIncludeVat"
                    checked={costPricesIncludeVat && invoiceAmountType === "incl"}
                    onChange={(e) => setCostDisplayMode(e.target.checked)}
                    disabled={invoiceAmountType === "excl"}
                    className="rounded"
                  />
                  <label htmlFor="costPricesIncludeVat" className={`text-sm ${invoiceAmountType === "excl" ? "text-gray-400" : "text-blue-700"}`}>
                    Supplier cost prices already include VAT
                    {invoiceAmountType === "excl" && <span className="text-xs ml-1">(N/A — invoice is Excl VAT)</span>}
                  </label>
                </div>
              </div>
            </div>

            {/* Delivery Fee */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <Label className="text-base font-semibold text-amber-800">Delivery Fee (Optional)</Label>
              <p className="text-xs text-amber-600 mb-3">
                This amount will be distributed across all items proportionally based on their total value
              </p>
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-2.5 text-gray-500">R</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={deliveryFee}
                    onChange={(e) => setDeliveryFee(e.target.value)}
                    placeholder="0.00"
                    className="pl-8"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="deliveryFeeIncludesVat"
                      checked={deliveryFeeIncludesVat}
                      onChange={(e) => setDeliveryFeeIncludesVat(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="deliveryFeeIncludesVat" className="text-sm text-amber-700">
                      Includes VAT
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="includeDeliveryFeeInTotals"
                      checked={includeDeliveryFeeInTotals}
                      onChange={(e) => setIncludeDeliveryFeeInTotals(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="includeDeliveryFeeInTotals" className="text-sm text-amber-700" data-testid="grv-include-delivery-in-totals-toggle">
                      Include in GRV totals
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-base font-semibold">Notes (Optional)</Label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes..."
                className="w-full mt-2 border rounded-md p-3 min-h-[80px]"
              />
            </div>

            {/* Action Button */}
            <div className="flex justify-end pt-4 border-t">
              <Button
                onClick={handleValidateHeader}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 px-8"
                size="lg"
              >
                {loading ? "Validating..." : "Continue to Items"}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Screen 2: Items */}
      {currentScreen === "items" && (
        <div className="space-y-4">
          {/* Summary Header + Search - Fixed sticky below nav bar */}
          <div className="sticky top-[88px] z-40 -mx-6 px-6 pt-2 pb-4 bg-gray-100/95 backdrop-blur-sm shadow-md border-b">
            <div className="w-full space-y-3">
              {/* Header Info */}
              <Card className="bg-white">
                <CardContent className="py-3">
                  <div className="flex justify-between items-center">
                    <div className="flex gap-6 text-sm">
                      <div>
                        <span className="text-gray-500">Supplier:</span>{" "}
                        <span className="font-semibold">{headerValidation?.supplier_details?.name}</span>
                        <span className="text-gray-500 ml-2">({headerValidation?.supplier_details?.supplier_number})</span>
                      </div>
                      {poNumber && (
                        <div>
                          <span className="text-gray-500">PO:</span>{" "}
                        <span className="font-semibold">{poNumber}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Invoice:</span>{" "}
                      <span className="font-semibold">{supplierInvoiceNumber}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Invoice Amount:</span>{" "}
                      <span className="font-semibold">R {parseFloat(invoiceAmount).toFixed(2)} ({invoiceAmountType})</span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setCurrentScreen("header")}>
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                </div>
              </CardContent>
            </Card>

              {/* Search Bar */}
              <Card className="bg-white">
                <CardContent className="py-3">
                  <div className="flex gap-4 items-center">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <Input
                        ref={productSearchRef}
                        value={productSearch}
                        onChange={(e) => {
                          setProductSearch(e.target.value);
                          searchProducts(e.target.value);
                        }}
                        onFocus={() => setShowProductSearch(true)}
                        onBlur={() => {
                          // Delay to allow click on dropdown items
                          setTimeout(() => setShowProductSearch(false), 200);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && productSearch.trim().length >= 2) {
                            e.preventDefault();
                            const q = productSearch.trim();
                            const seq = ++searchSeqRef.current;
                            clearTimeout(searchTimerRef.current);
                            axios.get(`${API}/products/search?q=${encodeURIComponent(q)}`).then((res) => {
                              if (seq !== searchSeqRef.current) return;
                              const list = res.data || [];
                              const exact = list.find((p) => [p.plu_code, p.barcode].map((v) => String(v || "").toLowerCase()).includes(q.toLowerCase()));
                              if (exact || list[0]) handleAddProduct(exact || list[0]);
                              else setProductSearchResults([]);
                            }).catch(() => toast.error("Search failed"));
                          }
                        }}
                        placeholder="Search product by PLU, barcode, or name to add..."
                        className={`pl-10 ${showProductSearch ? 'ring-2 ring-blue-500 border-blue-500' : ''}`}
                      />
                      {showProductSearch && productSearchResults.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                          {productSearchResults.map(product => (
                            <div
                              key={product.id}
                              className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                              onMouseDown={(e) => {
                                e.preventDefault(); // Prevent blur from firing before click
                                handleAddProduct(product);
                              }}
                            >
                              <div className="flex justify-between">
                                <div>
                                  <span className="font-medium">{product.name}</span>
                                  <span className="text-sm text-gray-500 ml-2">PLU: {product.plu_code}</span>
                                </div>
                                <div className="text-sm text-gray-600">
                                  Cost: R {(product.last_cost_price_excl_vat || product.cost_price || 0).toFixed(2)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {showProductSearch && productSearch.length >= 2 && productSearchResults.length === 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-gray-500">
                          No products found for "{productSearch}"
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowProductSearch(true);
                        setTimeout(() => {
                          productSearchRef.current?.focus();
                          productSearchRef.current?.select();
                        }, 50);
                      }}
                      className="flex-shrink-0"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Product
                    </Button>
                    <Button
                      variant="default"
                      onClick={() => handleOpenNewProductModal()}
                      className="flex-shrink-0 bg-green-600 hover:bg-green-700"
                      data-testid="grv-add-new-product-btn"
                    >
                      <PlusCircle className="h-4 w-4 mr-1" />
                      Add NEW Product
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Items Table */}
          <Card className="mb-4 mt-4">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right w-[100px]">Ordered</TableHead>
                    <TableHead className="text-right w-[120px]">Qty to Receive</TableHead>
                    <TableHead className="text-right w-[120px]">
                      Previous Cost
                      <span className="block text-xs text-gray-400 font-normal">
                        {costPricesIncludeVat && invoiceAmountType === "incl" ? "(incl VAT*)" : "(excl VAT)"}
                      </span>
                    </TableHead>
                    <TableHead className="text-right w-[120px]">
                      New Cost
                      <span className="block text-xs text-gray-400 font-normal">
                        {costPricesIncludeVat && invoiceAmountType === "incl" ? "(incl VAT*)" : "(excl VAT)"}
                      </span>
                    </TableHead>
                    <TableHead className="text-right w-[80px]">Markup %</TableHead>
                    <TableHead className="text-right w-[70px]" title="Gross profit % = (Sell excl − Cost excl) ÷ Sell excl × 100">GP %</TableHead>
                    <TableHead className="text-right w-[120px]">New Price</TableHead>
                    <TableHead className="w-[120px]">Expiry Date</TableHead>
                    <TableHead className="text-right w-[120px]">Total</TableHead>
                    <TableHead className="w-[60px] text-center" title="Print Shelf Talker">
                      <div className="flex items-center justify-center gap-1">
                        <Tag className="h-3 w-3" />
                        <span className="text-xs">Shelf</span>
                      </div>
                    </TableHead>
                    <TableHead className="w-[60px] text-center" title="Print Stock Labels (qty = qty received)">
                      <div className="flex items-center justify-center gap-1">
                        <Package className="h-3 w-3" />
                        <span className="text-xs">Labels</span>
                      </div>
                    </TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                {costPricesIncludeVat && (
                  <caption className="caption-top text-xs text-blue-600 text-left py-1">
                    * VAT conversion only applies to standard-rated items. Zero-rated (0% VAT) items show unchanged costs.
                  </caption>
                )}
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={14} className="text-center py-8 text-gray-500">
                        <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No items to receive. Search and add products above.</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item, index) => {
                      // Get VAT rate for this item
                      const vatRate = item.vat_category === "Zero-rated 0%" || item.vat_category === "Exempt" ? 0 : 0.15;
                      
                      // previous_cost is always stored EXCL VAT
                      // new_unit_cost is entered as INCL VAT when costPricesIncludeVat is true
                      // Convert both to same basis (excl VAT) for comparison
                      const newCostRaw = parseFloat(item.new_unit_cost) || 0;
                      const newCostExclVat = costPricesIncludeVat && vatRate > 0 
                        ? newCostRaw / (1 + vatRate) 
                        : newCostRaw;
                      const prevCostExclVat = item.previous_cost || 0;
                      
                      // Compare costs on same basis (both excl VAT)
                      const costIncreased = newCostExclVat > prevCostExclVat && prevCostExclVat > 0;
                      const costDecreased = newCostExclVat < prevCostExclVat && prevCostExclVat > 0 && newCostExclVat > 0;
                      const isSpecial = specials.some(s => s.product_id === item.product_id);
                      
                      return (
                        <TableRow key={index} className={`[&_td]:py-1.5 ${
                          isSpecial ? "bg-yellow-100 border-l-4 border-yellow-500" : 
                          costIncreased ? "bg-orange-50" : 
                          costDecreased ? "bg-green-50" :
                          (item.track_expiry ? "bg-red-50/30" : "")
                        }`}>
                          <TableCell className="font-mono text-sm align-middle">
                            {index + 1}
                            {isSpecial && <Sparkles className="h-3 w-3 text-yellow-600 ml-1 inline" />}
                          </TableCell>
                          <TableCell className="align-middle min-w-[220px]">
                            <div className="leading-tight">
                              <div className="font-medium flex flex-wrap items-center gap-x-2 gap-y-0.5 whitespace-nowrap">
                                <span className="whitespace-nowrap">{item.name}</span>
                                {item.is_direct_line && (
                                  <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0 rounded font-medium" data-testid={`grv-direct-badge-${index}`}>
                                    {item.plu_code} DIRECT{item.co_ticket_no ? ` · ${item.co_ticket_no}` : ""}
                                  </span>
                                )}
                                {item.track_expiry && (
                                  <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0 rounded font-medium">
                                    EXPIRY
                                  </span>
                                )}
                                {isSpecial && (
                                  <span className="text-[10px] bg-yellow-200 text-yellow-800 px-1.5 py-0 rounded font-bold animate-pulse">
                                    SPECIAL!
                                  </span>
                                )}
                                {/* Inline VAT category selector — the operator can flip a single
                                    line to Zero-rated / Exempt / Standard without leaving the GRV
                                    (great for reconciling a supplier invoice that treats specific
                                    items differently). Change here only affects THIS GRV line;
                                    use the Stock Master bulk-VAT tool to persist across future GRVs. */}
                                <Select
                                  value={item.vat_category || "Standard 15%"}
                                  onValueChange={(val) => handleItemChange(index, "vat_category", val)}
                                >
                                  <SelectTrigger
                                    className={`h-5 px-1.5 py-0 rounded font-medium text-[10px] border-0 w-auto min-w-[70px] gap-1 ${
                                      item.vat_category === "Zero-rated 0%"
                                        ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                        : item.vat_category === "Exempt"
                                          ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                                          : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                    }`}
                                    data-testid={`grv-vat-category-${index}`}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Standard 15%">Standard 15%</SelectItem>
                                    <SelectItem value="Zero-rated 0%">Zero-rated 0%</SelectItem>
                                    <SelectItem value="Exempt">Exempt</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="text-xs text-gray-500 flex flex-wrap items-center gap-x-2 gap-y-0">
                                <span className="whitespace-nowrap">PLU: {item.plu_code} | Stock: {item.current_stock}</span>
                                {missingImageIds.has(item.product_id) && !item.is_direct_line && (
                                  <GrvPasteImageButton
                                    item={item}
                                    index={index}
                                    onUploaded={(pid) => setMissingImageIds((s) => { const n = new Set(s); n.delete(pid); return n; })}
                                  />
                                )}
                                {!item.has_barcode && (
                                  <span className="inline-flex items-center">
                                    <input
                                      type="text"
                                      placeholder="+ Scan barcode"
                                      className="w-32 h-5 text-xs border border-dashed border-gray-300 rounded px-1 focus:border-blue-400 focus:outline-none"
                                      value={item.new_barcode || ""}
                                      onChange={(e) => handleItemChange(index, "new_barcode", e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && item.new_barcode) {
                                          e.preventDefault();
                                          // Save barcode immediately
                                          axios.post(`${API}/products/${item.product_id}/add-barcode`, { barcode: item.new_barcode })
                                            .then(() => {
                                              toast.success(`Barcode added to ${item.name}`);
                                              handleItemChange(index, "has_barcode", true);
                                              handleItemChange(index, "barcode", item.new_barcode);
                                            })
                                            .catch(err => toast.error(err.response?.data?.detail || "Failed to add barcode"));
                                        }
                                      }}
                                      data-testid={`grv-barcode-input-${index}`}
                                    />
                                  </span>
                                )}
                                {item.has_barcode && item.barcode && (
                                  <span className="text-green-600 text-xs">BC: {item.barcode}</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right align-middle">{item.quantity_ordered || "-"}</TableCell>
                          <TableCell className="align-middle">
                            <Input
                              type="number"
                              step="1"
                              value={item.quantity_to_receive}
                              onChange={(e) => handleItemChange(index, "quantity_to_receive", e.target.value)}
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={(e) => {
                                if (e.key === 'Tab' && !e.shiftKey) {
                                  // Find the next quantity input
                                  const nextIndex = index + 1;
                                  if (nextIndex < items.length) {
                                    e.preventDefault();
                                    const nextInput = document.querySelector(`[data-grv-qty-index="${nextIndex}"]`);
                                    if (nextInput) nextInput.focus();
                                  }
                                }
                              }}
                              placeholder=""
                              className="w-24 h-8 text-right ml-auto [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              data-grv-qty-index={index}
                              data-testid={`grv-qty-input-${index}`}
                              autoFocus={index === 0}
                            />
                          </TableCell>
                          <TableCell className="text-right text-gray-500 text-sm align-middle">
                            {/* Show previous cost - convert to incl VAT if costPricesIncludeVat is true */}
                            {(() => {
                              const prevCost = item.previous_cost || 0;
                              const vatRate = item.vat_category === "Zero-rated 0%" || item.vat_category === "Exempt" ? 0 : 0.15;
                              const displayPrevCost = costPricesIncludeVat && vatRate > 0 
                                ? prevCost * (1 + vatRate) 
                                : prevCost;
                              return `R ${displayPrevCost.toFixed(2)}`;
                            })()}
                          </TableCell>
                          <TableCell className="align-middle">
                            <div className="relative">
                              <Input
                                type="number"
                                step="0.01"
                                value={item.new_unit_cost}
                                onChange={(e) => handleItemChange(index, "new_unit_cost", e.target.value)}
                                data-testid={`grv-cost-input-${index}`}
                                placeholder=""
                                className={`w-28 h-8 text-right ml-auto ${
                                  costIncreased ? "border-orange-500" : 
                                  isSpecial ? "border-yellow-500 bg-yellow-50" : ""
                                }`}
                                tabIndex={-1}
                              />
                              {costIncreased && (
                                <AlertTriangle className="absolute right-[-20px] top-2 h-4 w-4 text-orange-500" />
                              )}
                              {isSpecial && (
                                <Sparkles className="absolute right-[-20px] top-2 h-4 w-4 text-yellow-500" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="align-middle">
                            <Input
                              type="number"
                              min="0"
                              step="0.1"
                              value={item.markup_percent || ""}
                              onChange={(e) => handleItemChange(index, "markup_percent", e.target.value)}
                              placeholder=""
                              className="w-20 h-8 text-right ml-auto"
                              tabIndex={-1}
                              data-testid={`markup-input-${index}`}
                            />
                          </TableCell>
                          <TableCell className="text-right align-middle">
                            {(() => {
                              // GP % = (Selling excl VAT − Cost excl VAT) / Selling excl VAT * 100
                              const sellInclOrExcl = parseFloat(item.new_selling_price) || 0;
                              const lineVat = item.vat_category === "Zero-rated 0%" || item.vat_category === "Exempt" ? 0 : 0.15;
                              // new_selling_price is stored INCL VAT — convert to excl
                              const sellExcl = lineVat > 0 ? sellInclOrExcl / (1 + lineVat) : sellInclOrExcl;
                              // new_unit_cost may be stored incl or excl depending on toggle
                              const rawCost = parseFloat(item.new_unit_cost) || 0;
                              const costExcl = costPricesIncludeVat && lineVat > 0 ? rawCost / (1 + lineVat) : rawCost;
                              if (sellExcl <= 0) return <span className="text-xs text-gray-400">-</span>;
                              const gp = ((sellExcl - costExcl) / sellExcl) * 100;
                              const colour = gp >= 25 ? "text-green-600" : gp >= 15 ? "text-amber-600" : "text-red-600";
                              return (
                                <span className={`font-semibold text-sm ${colour}`} data-testid={`gp-pct-${index}`}>
                                  {gp.toFixed(1)}%
                                </span>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="align-middle">
                            <div className="flex items-center justify-end gap-1">
                              {item.price_locked && (
                                <Lock className="h-4 w-4 text-sky-600 shrink-0" title="Frozen price — only an admin can change it" data-testid={`grv-price-locked-${index}`} />
                              )}
                              <Input
                                type="number"
                                step="0.01"
                                value={item.new_selling_price || ""}
                                onChange={(e) => handleItemChange(index, "new_selling_price", e.target.value)}
                                placeholder=""
                                className={`w-28 h-8 text-right ${item.price_locked ? "border-sky-400 bg-sky-50" : ""}`}
                                tabIndex={-1}
                                data-testid={`grv-sell-price-input-${index}`}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="align-middle">
                            {item.track_expiry ? (
                              <Input
                                type="date"
                                value={item.expiry_date || ""}
                                onChange={(e) => handleItemChange(index, "expiry_date", e.target.value)}
                                className="w-32 h-8"
                                data-testid={`expiry-date-input-${index}`}
                              />
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium align-middle">
                            R {(item.total_cost || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-center align-middle">
                            <Checkbox
                              checked={item.print_shelf_talker}
                              onCheckedChange={(checked) => handleItemChange(index, "print_shelf_talker", checked)}
                              className="data-[state=checked]:bg-blue-600"
                              title="Print 1 shelf talker for this product"
                            />
                          </TableCell>
                          <TableCell className="text-center align-middle">
                            <div className="flex items-center justify-center gap-1">
                              <Checkbox
                                checked={item.print_stock_labels}
                                onCheckedChange={(checked) => handleItemChange(index, "print_stock_labels", checked)}
                                className="data-[state=checked]:bg-green-600"
                                title={item.has_barcode ? "Product already has a barcode — you can still print labels if you want extra tags" : "Print stock labels"}
                              />
                              {item.print_stock_labels && (
                                <Input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={item.stock_labels_qty}
                                  onChange={(e) => handleItemChange(index, "stock_labels_qty", e.target.value)}
                                  onWheel={(e) => e.target.blur()}
                                  placeholder={String(item.quantity_to_receive || 0)}
                                  className="w-14 h-7 text-xs text-center px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  title="Override # of labels to print (blank = same as Qty to Receive)"
                                  tabIndex={-1}
                                  data-testid={`labels-qty-input-${index}`}
                                />
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="align-middle">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveItem(index)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                              tabIndex={-1}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Totals and Submit */}
          <Card>
            <CardContent className="py-4">
              <div className="flex justify-between items-start">
                {/* Totals */}
                <div className="space-y-2">
                  <div className="flex gap-8 items-start">
                    <div>
                      <span className="text-gray-500">Items Subtotal (Excl VAT):</span>
                      <span className="font-semibold ml-2" data-testid="grv-items-subtotal-excl-vat">R {totals.itemSubtotal}</span>
                    </div>
                    {parseFloat(deliveryFee) > 0 && (
                      <div>
                        <span className="text-gray-500">Delivery Fee (Excl VAT){includeDeliveryFeeInTotals ? ":" : " - excluded from GRV total:"}</span>
                        <span className="font-semibold ml-2" data-testid="grv-delivery-fee-excl-vat">R {totals.deliveryFeeExclVat}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Subtotal (Excl VAT):</span>
                      <span className="font-semibold ml-2" data-testid="grv-subtotal-excl-vat">R {totals.subtotal}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">VAT ({includeDeliveryFeeInTotals ? "on applicable items + delivery" : "on applicable items only"}):</span>
                      <span className="font-semibold ml-2" data-testid="grv-vat-total">R {totals.vat}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Total (Incl VAT):</span>
                      <span className="font-bold ml-2 text-lg" data-testid="grv-total-incl-vat">R {totals.total}</span>
                    </div>
                  </div>
                  
                  {/* Delivery Fee Info */}
                  {parseFloat(deliveryFee) > 0 && (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-amber-800">
                          <span className="font-medium">Delivery Fee:</span>
                          <span className="ml-2 font-bold">R {parseFloat(deliveryFee).toFixed(2)}</span>
                          <span className="text-xs ml-1">({deliveryFeeIncludesVat ? "incl VAT" : "excl VAT"})</span>
                        </div>
                        <div className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded" data-testid="grv-delivery-fee-breakdown">
                          Excl VAT: R {deliveryDistribution.feeExclVat.toFixed(2)} | VAT: R {(deliveryDistribution.feeExclVat * 0.15).toFixed(2)} | {includeDeliveryFeeInTotals ? "Included in totals" : "Excluded from totals"}
                        </div>
                      </div>
                      {deliveryDistribution.items.length > 0 && (
                        <div className="text-xs text-amber-700 space-y-1">
                          <div className="font-medium mb-1">Distribution Preview (added to cost per unit):</div>
                          <div className="max-h-24 overflow-y-auto space-y-0.5">
                            {deliveryDistribution.items.slice(0, 5).map((dist, idx) => (
                              <div key={idx} className="flex justify-between items-center bg-amber-100/50 px-2 py-0.5 rounded">
                                <span className="truncate max-w-[200px]">{dist.name}</span>
                                <span className="font-mono">
                                  R {dist.original_cost.toFixed(2)} + R {dist.delivery_fee_per_unit.toFixed(2)} = 
                                  <span className="font-bold text-amber-900"> R {dist.landed_cost.toFixed(2)}</span>
                                </span>
                              </div>
                            ))}
                            {deliveryDistribution.items.length > 5 && (
                              <div className="text-center text-amber-600">
                                ... and {deliveryDistribution.items.length - 5} more items
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="border border-sky-200 bg-sky-50/80 rounded-lg p-3" data-testid="grv-calculation-audit-panel">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-sky-900" data-testid="grv-calculation-audit-heading">
                        GRV Calculation Audit
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCalculationAudit((prev) => !prev)}
                        data-testid="grv-calculation-audit-toggle-button"
                      >
                        {showCalculationAudit ? "Hide details" : "Show details"}
                      </Button>
                    </div>
                    <p className="text-xs text-sky-700 mt-1" data-testid="grv-calculation-audit-description">
                      Live formula trace before posting this GRV.
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
                      <div className="bg-white rounded border p-2" data-testid="grv-audit-items-subtotal-card">
                        <div className="text-gray-500">Items Subtotal</div>
                        <div className="font-semibold" data-testid="grv-audit-items-subtotal-value">R {calculationAudit.itemsSubtotalExclVat.toFixed(2)}</div>
                      </div>
                      <div className="bg-white rounded border p-2" data-testid="grv-audit-items-vat-card">
                        <div className="text-gray-500">VAT on Items</div>
                        <div className="font-semibold" data-testid="grv-audit-items-vat-value">R {calculationAudit.itemsVat.toFixed(2)}</div>
                      </div>
                      <div className="bg-white rounded border p-2" data-testid="grv-audit-delivery-excl-card">
                        <div className="text-gray-500">Delivery Excl VAT</div>
                        <div className="font-semibold" data-testid="grv-audit-delivery-excl-value">R {calculationAudit.deliveryFeeExclVat.toFixed(2)}</div>
                      </div>
                      <div className="bg-white rounded border p-2" data-testid="grv-audit-delivery-vat-card">
                        <div className="text-gray-500">Delivery VAT</div>
                        <div className="font-semibold" data-testid="grv-audit-delivery-vat-value">R {calculationAudit.deliveryFeeVat.toFixed(2)}</div>
                      </div>
                    </div>

                    {showCalculationAudit && (
                      <div className="mt-3 space-y-2 text-xs" data-testid="grv-calculation-audit-details">
                        <div className="bg-white rounded border p-2 space-y-1">
                          <div className="flex justify-between" data-testid="grv-audit-subtotal-formula-row">
                            <span className="text-gray-600">Subtotal formula:</span>
                            <span className="font-mono">R {calculationAudit.itemsSubtotalExclVat.toFixed(2)} {includeDeliveryFeeInTotals ? "+ R " + calculationAudit.deliveryFeeExclVat.toFixed(2) : "+ R 0.00"}</span>
                          </div>
                          <div className="flex justify-between" data-testid="grv-audit-vat-formula-row">
                            <span className="text-gray-600">VAT formula:</span>
                            <span className="font-mono">R {calculationAudit.itemsVat.toFixed(2)} {includeDeliveryFeeInTotals ? "+ R " + calculationAudit.deliveryFeeVat.toFixed(2) : "+ R 0.00"}</span>
                          </div>
                          <div className="flex justify-between" data-testid="grv-audit-total-formula-row">
                            <span className="text-gray-600">Total formula:</span>
                            <span className="font-mono">R {calculationAudit.subtotalExclVat.toFixed(2)} + R {calculationAudit.vatTotal.toFixed(2)}</span>
                          </div>
                          <div className="border-t pt-1 flex justify-between font-semibold" data-testid="grv-audit-comparison-row">
                            <span>{calculationAudit.comparisonBasis} vs Invoice</span>
                            <span className={calculationAudit.isBalanced ? "text-green-700" : "text-orange-700"} data-testid="grv-audit-variance-value">R {calculationAudit.variance.toFixed(2)}</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                          <div className={`rounded border p-2 ${calculationAudit.subtotalCheckPass ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="grv-audit-subtotal-check">
                            Subtotal check: {calculationAudit.subtotalCheckPass ? "PASS" : "FAIL"}
                          </div>
                          <div className={`rounded border p-2 ${calculationAudit.vatCheckPass ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="grv-audit-vat-check">
                            VAT check: {calculationAudit.vatCheckPass ? "PASS" : "FAIL"}
                          </div>
                          <div className={`rounded border p-2 ${calculationAudit.totalCheckPass ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="grv-audit-total-check">
                            Total check: {calculationAudit.totalCheckPass ? "PASS" : "FAIL"}
                          </div>
                        </div>

                        <div className="bg-white rounded border p-2" data-testid="grv-audit-line-item-preview">
                          <div className="font-medium mb-2">Line VAT Preview (first 6 items)</div>
                          <div className="space-y-1 max-h-28 overflow-y-auto">
                            {calculationAudit.lineItems.slice(0, 6).map((line) => (
                              <div key={line.id} className="flex justify-between text-[11px]">
                                <span className="truncate max-w-[220px]">{line.name}</span>
                                <span className="font-mono">{line.qty} × R {line.unitCostExclVat.toFixed(2)} | VAT {line.vatRate === 0 ? "0%" : "15%"} = R {line.lineVat.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className={`flex items-center gap-2 ${totals.isBalanced ? "text-green-600" : "text-orange-600"}`}>
                    {totals.isBalanced ? (
                      <>
                        <Check className="h-5 w-5" />
                        <span>Amounts balanced</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="h-5 w-5" />
                        <span>Variance: R {totals.variance}</span>
                        {canDistributeVariance() && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={distributeVariance}
                            className="ml-2 border-orange-400 text-orange-700 hover:bg-orange-50"
                            data-testid="distribute-variance-btn"
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Distribute Variance
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={saveDraft}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="save-draft-bottom-btn"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Draft
                  </Button>
                  <Button
                    onClick={handleSubmitGRV}
                    disabled={submitting || items.length === 0}
                    className="bg-green-600 hover:bg-green-700 px-8"
                    size="lg"
                  >
                    {submitting ? "Processing..." : "Complete GRV"}
                    <Check className="h-5 w-5 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Price Increase Alert Dialog */}
      <Dialog open={showPriceAlerts} onOpenChange={setShowPriceAlerts}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              Price Increase Alert
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              The following items have cost price increases from previous orders:
            </p>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {priceAlerts.map((alert, index) => (
                <div key={index} className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="font-medium">{alert.name}</div>
                  <div className="text-sm text-gray-600">PLU: {alert.plu_code}</div>
                  <div className="flex gap-4 mt-1 text-sm">
                    <span>Previous: R {alert.previous_cost.toFixed(2)}</span>
                    <span className="text-orange-600 font-medium">
                      New: R {alert.new_cost.toFixed(2)} (+{alert.change_percent}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPriceAlerts(false)}>
              Go Back & Edit
            </Button>
            <Button onClick={submitGRV} className="bg-orange-600 hover:bg-orange-700">
              Accept & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shelf Talker Print Modal */}
      <ShelfTalkerModal
        open={showShelfTalkerModal}
        onClose={() => {
          setShowShelfTalkerModal(false);
          setSavedGrvItems([]);
        }}
        items={savedGrvItems}
      />

      {/* Print GRV Prompt */}
      <Dialog open={showPrintGrvPrompt} onOpenChange={setShowPrintGrvPrompt}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5 text-blue-600" />
              GRV created — print anything?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            GRV <span className="font-semibold text-blue-600">{lastCreatedGrv?.grv_number}</span> was created. Choose what to print:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => { sendStockLabelsToStorePrinter(lastCreatedGrv); }}
              className="text-purple-700 border-purple-300 hover:bg-purple-50 justify-start h-auto py-3"
              data-testid="send-stock-labels-to-store-btn"
              title="Queue stock labels to print on the store's printer (works from home)"
            >
              <Printer className="h-4 w-4 mr-2 flex-shrink-0" />
              <div className="flex flex-col items-start text-left">
                <span className="font-semibold leading-tight">Stock Labels</span>
                <span className="text-xs opacity-75 leading-tight">→ Store printer</span>
              </div>
            </Button>
            <Button
              variant="outline"
              onClick={() => { printStockLabels(lastCreatedGrv); }}
              className="text-amber-700 border-amber-300 hover:bg-amber-50 justify-start h-auto py-3"
              data-testid="print-stock-labels-btn"
            >
              <Printer className="h-4 w-4 mr-2 flex-shrink-0" />
              <div className="flex flex-col items-start text-left">
                <span className="font-semibold leading-tight">Stock Labels</span>
                <span className="text-xs opacity-75 leading-tight">→ This device</span>
              </div>
            </Button>
            <Button
              onClick={() => { handlePrintGrv(lastCreatedGrv); setShowPrintGrvPrompt(false); setLastCreatedGrv(null); }}
              className="justify-start h-auto py-3"
              data-testid="print-grv-confirm-btn"
            >
              <Printer className="h-4 w-4 mr-2 flex-shrink-0" />
              <div className="flex flex-col items-start text-left">
                <span className="font-semibold leading-tight">GRV Slip</span>
                <span className="text-xs opacity-75 leading-tight">A4 paper trail</span>
              </div>
            </Button>
          </div>

          <DialogFooter className="pt-2">
            <Button
              variant="ghost"
              onClick={() => { setShowPrintGrvPrompt(false); setLastCreatedGrv(null); }}
              data-testid="print-grv-skip-btn"
            >
              Skip — don't print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GRV History Modal */}
      <GRVHistoryModal
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
      />

      <AiInvoiceImportModal
        open={aiImportOpen}
        onClose={() => setAiImportOpen(false)}
        onApply={(result) => {
          // Pre-fill supplier + invoice number + total if user matched a supplier
          if (result.supplier_match?.id && !selectedSupplier) {
            setSelectedSupplier({ id: result.supplier_match.id, name: result.supplier_match.name });
          }
          if (result.invoice_number && !supplierInvoiceNumber) {
            setSupplierInvoiceNumber(result.invoice_number);
          }
          if (result.invoice_total_incl_vat && !invoiceAmount) {
            setInvoiceAmount(String(result.invoice_total_incl_vat));
            setInvoiceAmountType("incl");
          }
          // Append AI rows to the items list. Reuse the same shape addItem produces.
          setItems((prev) => {
            const next = [...prev];
            result.rows.forEach((r) => {
              const exists = next.find((it) => it.product_id === r.product_id);
              if (exists) {
                exists.quantity_to_receive = (Number(exists.quantity_to_receive) || 0) + (Number(r.quantity_to_receive) || 0);
                exists.new_unit_cost = r.new_unit_cost;
              } else {
                next.push({
                  product_id: r.product_id,
                  name: r.product_name,
                  plu_code: r.plu_code,
                  current_stock: r.current_stock,
                  quantity_ordered: 0,
                  quantity_to_receive: r.quantity_to_receive,
                  previous_unit_cost: r.previous_unit_cost,
                  new_unit_cost: r.new_unit_cost,
                  markup_percent: 0,
                  new_selling_price: 0,
                  print_shelf_talker: false,
                  print_stock_labels: false,
                  has_barcode: false,
                  ai_imported: true,
                  ai_invoice_description: r.ai_invoice_description,
                  total_cost: (Number(r.quantity_to_receive) || 0) * (Number(r.new_unit_cost) || 0),
                });
              }
            });
            return next;
          });
          toast.success(`Imported ${result.rows.length} line${result.rows.length === 1 ? "" : "s"} from AI`);
        }}
      />

      {/* Full Product Detail Modal for creating new products */}
      {newProductForModal && (
        <ProductDetailModal
          open={showNewProductModal}
          onClose={() => {
            setShowNewProductModal(false);
            setNewProductForModal(null);
          }}
          product={newProductForModal}
          onSave={handleSaveNewProduct}
          categories={categories}
          departments={departments}
          onRefreshLists={handleRefreshLists}
          isNew={true}
        />
      )}
    </div>
  );
};

export default StockReceiving;
